import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useChangeBookingPackage } from './events.api';
import { PackagePicker } from './package-picker';

/**
 * Move one booking onto a different package of the same event.
 *
 * Shaped on `MoveBookingDialog`, which answers the same question in the other
 * dimension. Only active packages are offered — a retired one can still be
 * named by an old booking, but nothing new is sold onto it.
 */
export function ChangePackageDialog({
  booking,
  open,
  onOpenChange,
  onChanged,
}: {
  booking: {
    id: string;
    eventId: string | null;
    packageId: string | null;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [adjust, setAdjust] = useState(true);

  const change = useChangeBookingPackage();

  if (!booking) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('events.changePackage')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('events.changePackageBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <PackagePicker
            eventId={open ? booking.eventId : null}
            value={selectedId}
            onChange={setSelectedId}
            excludeId={booking.packageId}
            disabled={change.isPending}
          />

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-4">
              <Label className="text-xs font-semibold">
                {t('events.adjustPurchaseAmount')}
              </Label>
              <Switch checked={adjust} onCheckedChange={setAdjust} />
            </div>
            {/* This switch decides whether money moves, so it says which of
                the two it is doing rather than naming itself twice. */}
            <p className="text-muted-foreground text-xs">
              {adjust
                ? t('events.adjustPurchaseOn')
                : t('events.adjustPurchaseOff')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">{t('events.reason')}</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={!selectedId || change.isPending}
            onClick={(clickEvent) => {
              clickEvent.preventDefault();
              change.mutate(
                {
                  bookingId: booking.id,
                  eventPackageId: selectedId!,
                  reason,
                  adjustPurchaseAmount: adjust,
                },
                {
                  /*
                   * Not always "saved". Even with the switch on, the server
                   * leaves the money alone for a comped booking, a benefit
                   * redemption and one under payment review — and says which
                   * in the message it returns. Show that, not our own.
                   */
                  onSuccess: (message) => {
                    toast.success(message || t('events.packageChanged'));
                    onOpenChange(false);
                    onChanged();
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('events.bookingActionFailed')
                    ),
                }
              );
            }}
          >
            {change.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              t('events.changePackage')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
